import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import { PreviewCanvas } from './components/PreviewCanvas';
import { PDFExport } from '@progress/kendo-react-pdf';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CommonModal from 'src/common/config/CommonModal';
import { GenerateIdeasLoader } from '../keywordResearch/blogPostIdeas/GenerateIdeasLoader';
import { websiteAuditDashboardText } from 'src/common/config/TextMessage';
import {
  PDFBuilderCSS,
  NavBarCSS,
  SideBarCSS
} from '../pdfBuilder/styles/style';
import { usePdfData } from './hooks/usePdfData';
import { ErrorState } from './components/ErrorState';
import { updatePDFConfig } from 'src/redux/dashboard';
import { setSearchesDetails, setIsUnlocked } from 'src/redux/keywordResearch';
import { setMapScreenshots, setMapScreenshotSignal } from 'src/redux/localSeo';
import { PDF_TEXTS } from './utils/texts';
import { PDFSection } from './types';

const COMPARE = 'current';
const DEFAULT_FILE_NAME = 'PDF Report';

const PDFBuilderPublicRoute = () => {
  const { reportId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pdfExportRef = useRef<PDFExport>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfFileName, setPdfFileName] = useState(DEFAULT_FILE_NAME);
  const [reportMetaData, setReportMetaData] = useState<{
    dateFormat: string;
    siteUrl: string;
  }>({ dateFormat: '', siteUrl: '' });
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);

  const pdfReportData = useSelector(
    (state: RootState) => state.dashboard.pdfReportData
  );
  const pdfConfig = useSelector(
    (state: RootState) => state.dashboard.pdfConfig
  );

  // Sync config from API response to Redux state (mirrors PDFBuilderContainer logic)
  useEffect(() => {
    if (pdfReportData?.config) {
      const {
        ai_summary_options,
        sections_visibility,
        custom_icon,
        email_options
      } = pdfReportData.config;

      const { isGsc, dateFormat, siteUrl } = pdfReportData;

      const includedSections: PDFSection[] = [];
      if (sections_visibility) {
        if (sections_visibility.ai_summary)
          includedSections.push(PDFSection.AI_SUMMARY);
        if (sections_visibility.site_overview)
          includedSections.push(PDFSection.OVERVIEW);
        if (sections_visibility.site_audit_overview)
          includedSections.push(PDFSection.OVERVIEW);
        if (sections_visibility.seo_tasks)
          includedSections.push(PDFSection.TASKS);
        if (sections_visibility.scanned_pages)
          includedSections.push(PDFSection.SCANNED_PAGES);
        if (sections_visibility.ai_visibility)
          includedSections.push(PDFSection.AI_VISIBILITY);
        if (sections_visibility.keyword_tracking)
          includedSections.push(PDFSection.KEYWORD_TRACKING);
        if (sections_visibility.local_seo)
          includedSections.push(PDFSection.LOCAL_SEO);
      }

      // Build aiSummary config: merge options if present, then always sync previewText
      const aiSummaryUpdate = ai_summary_options
        ? {
            ...pdfConfig.aiSummary,
            goal: ai_summary_options.goal,
            tone: ai_summary_options.tone,
            format: ai_summary_options.format
          }
        : { ...pdfConfig.aiSummary };

      // Sync previewText from API response (field is camelCase: aiSummary)
      if (pdfReportData.aiSummary) {
        aiSummaryUpdate.previewText = pdfReportData.aiSummary;
      }

      dispatch(
        updatePDFConfig({
          includedSections:
            includedSections.length > 0
              ? includedSections
              : pdfConfig.includedSections,
          aiSummary: aiSummaryUpdate,
          customIcon: custom_icon || pdfConfig.customIcon,
          isGsc: isGsc
        })
      );

      setPdfFileName(() => {
        let fileName = email_options?.fileName.trim();

        if (fileName.endsWith(`.pdf`)) {
          fileName = fileName;
        } else {
          fileName = `${fileName}.pdf`;
        }
        return fileName || DEFAULT_FILE_NAME;
      });

      setReportMetaData({
        dateFormat: dateFormat,
        siteUrl: siteUrl
      });

      // For public route, populate searchesDetails so section visibility selector
      // doesn't block AI Summary, AI Visibility, and Keyword Tracking sections.
      // The server config already determines which sections should be visible.
      dispatch(
        setSearchesDetails({
          keywordLimit: sections_visibility?.keyword_tracking ? 1 : 0,
          totalAiSlots:
            sections_visibility?.ai_summary ||
            sections_visibility?.ai_visibility
              ? 1
              : 0
        })
      );

      // Unlock keyword tracking for public route (bypasses plan-based check in PdfCards)
      if (sections_visibility?.keyword_tracking) {
        dispatch(setIsUnlocked(true));
      }
    }
  }, [pdfReportData?.config]);

  const {
    loading: isDataLoading,
    error: dataError,
    localSeoError,
    localSeoLoading,
    refetch
  } = usePdfData(reportId as string, COMPARE, true);

  // Responsive scaling: fit the 1130px preview canvas into the available width
  useEffect(() => {
    if (isDataLoading) return;
    const container = previewContainerRef.current;
    if (!container) return;

    const CANVAS_TOTAL_WIDTH = 1178; // 1130px canvas + 2×24px (2rem) padding

    const updateScale = () => {
      const availableWidth = container.offsetWidth;
      setPreviewScale(Math.min(1, availableWidth / CANVAS_TOTAL_WIDTH));
    };

    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(container);
    return () => ro.disconnect();
  }, [isDataLoading]);

  // Track the latest map screenshots and local SEO data for the export process
  const localSeoState = useSelector((state: RootState) => state.localSeo);
  const localSeoReportData = useSelector(
    (state: RootState) => state.dashboard.localSeoReportData
  );
  const mapScreenshots = localSeoState.mapScreenshots;
  const mapScreenshotsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    mapScreenshotsRef.current = mapScreenshots;
  }, [mapScreenshots]);

  const handleGeneratePDF = () => {
    if (!pdfExportRef.current) return;

    setIsGenerating(true);

    // ── Step 1: Signal LocalRankingMap to capture screenshots ──
    dispatch(setMapScreenshots({})); // Clear old ones
    dispatch(setMapScreenshotSignal('capturing'));

    // Calculate how many screenshots we expect
    const keywords = localSeoReportData?.mapRank?.keywords || [];
    const includeAll = !!pdfConfig.includeAllKeywords;
    const expectedCount =
      includeAll && keywords.length > 0
        ? keywords.length
        : keywords.length > 0
        ? 1
        : 0;

    // ── Step 2: Wait for all captures to complete, then save PDF ───────
    let waited = 0;
    const maxWait = 15000;
    const pollInterval = 400;

    const waitAndSave = (currentScreenshots: Record<string, string>) => {
      const capturedCount = Object.keys(currentScreenshots).length;

      if (capturedCount >= expectedCount || expectedCount === 0) {
        dispatch(setMapScreenshotSignal('done'));

        setTimeout(() => {
          if (pdfExportRef.current) {
            pdfExportRef.current.save();
            setTimeout(() => {
              setIsGenerating(false);
              dispatch(setMapScreenshotSignal('idle'));
            }, 4000);
          } else {
            setIsGenerating(false);
          }
        }, 2000);
      } else if (waited >= maxWait) {
        console.warn(
          `PDF: Map capture timed out (${capturedCount}/${expectedCount})`
        );
        dispatch(setMapScreenshotSignal('done'));
        setTimeout(() => {
          if (pdfExportRef.current) {
            pdfExportRef.current.save();
            setTimeout(() => {
              setIsGenerating(false);
              dispatch(setMapScreenshotSignal('idle'));
            }, 4000);
          } else {
            setIsGenerating(false);
          }
        }, 2000);
      } else {
        waited += pollInterval;
        setTimeout(() => {
          waitAndSave(mapScreenshotsRef.current);
        }, pollInterval);
      }
    };

    setTimeout(() => {
      waitAndSave(mapScreenshotsRef.current);
    }, 1000);
  };

  if (isDataLoading) {
    return (
      <Box sx={SideBarCSS.fullPageLoading}>
        <CircularProgress size={40} sx={{ color: 'var(--color-primary)' }} />
        <Typography sx={SideBarCSS.loadingText}>
          {PDF_TEXTS.preview.loadingReport}
        </Typography>
        <Typography
          sx={{
            ...SideBarCSS.loadingText,
            fontSize: 'var(--font-size-14)',
            fontWeight: 400,
            color: 'var(--color-text-tertiary)',
            mt: 0
          }}
        >
          {PDF_TEXTS.preview.thisMayTakeAFewSeconds}
        </Typography>
      </Box>
    );
  }

  if (dataError) {
    return <ErrorState onRetry={refetch} message={dataError} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-subtle)'
      }}
    >
      {/* Toolbar with Generate PDF button */}
      <Box
        sx={{
          ...NavBarCSS.container,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0
        }}
      >
        <Box
          key="logo"
          component="img"
          onClick={() => navigate('/')}
          sx={NavBarCSS.logoImage}
          src="/static/images/logo/new-logo.svg"
          alt="SEOSpace"
        />
        <Button
          variant="contained"
          onClick={handleGeneratePDF}
          sx={NavBarCSS.generateBtn}
        >
          Generate PDF
        </Button>
      </Box>

      {/* Preview — scales down on smaller viewports */}
      <Box
        ref={previewContainerRef}
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          py: { xs: 2, md: 3 },
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            zoom: previewScale,
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
            borderRadius: '8px',
            backgroundColor: 'white'
          }}
        >
          <PreviewCanvas
            siteId={reportId}
            compare={COMPARE}
            selectedIcon={pdfConfig.customIcon}
            isExporting={true}
            localSeoError={localSeoError}
            localSeoLoading={localSeoLoading}
            reportMetaData={reportMetaData}
          />
        </Box>
      </Box>

      {/* Hidden Render Container for PDF Capture */}
      <div style={PDFBuilderCSS.hiddenRenderContainer}>
        <PDFExport
          ref={pdfExportRef}
          paperSize="A4"
          scale={0.5}
          margin={0}
          fileName={pdfFileName}
          forcePageBreak=".force-page-break"
          keepTogether=".keep-together"
        >
          <Box sx={PDFBuilderCSS.hiddenPdfWrapper}>
            <PreviewCanvas
              siteId={reportId}
              compare={COMPARE}
              selectedIcon={pdfConfig.customIcon}
              isExporting={true}
              localSeoError={null}
              localSeoLoading={false}
              reportMetaData={reportMetaData}
            />
          </Box>
        </PDFExport>
      </div>

      {/* Generating loader modal */}
      <CommonModal
        open={isGenerating}
        handCloseModal={() => {}}
        title=""
        closebtn={false}
        blur={false}
      >
        <GenerateIdeasLoader
          title={websiteAuditDashboardText?.pdfGeneratingText}
          isPdfLoader={true}
        />
      </CommonModal>
    </Box>
  );
};

export default PDFBuilderPublicRoute;
